import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { inviteQuery } from '@/features/public-booking/api';
import { BookingFlow } from '@/features/public-booking/booking-flow';
import { ensureNamespace } from '@/lib/i18n';

/**
 * A single-use booking link.
 *
 * The code stands in for the username and slug, so an invitation-only event
 * type is reachable here and nowhere else. A spent or expired link says so
 * rather than showing a 404, because the person clicking it twice is usually
 * the invitee wondering whether the first click worked.
 */
export const Route = createFileRoute('/i/$code')({
  loader: async ({ context, params }) => {
    await ensureNamespace('booking');
    return context.queryClient.ensureQueryData(inviteQuery(params.code));
  },
  component: InvitePage,
});

function InvitePage() {
  const { code } = Route.useParams();
  const { t } = useTranslation(['booking', 'common']);
  const { data } = useQuery(inviteQuery(code));

  if (!data) return null;

  if (data.state !== 'ok' || !data.host || !data.eventType || !data.username) {
    const reason = data.state === 'ok' ? 'unknown' : data.state;
    return (
      <main className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-lg font-medium">{t(`booking:invite.${reason}Title`)}</h1>
        <p className="mt-2 text-sm text-neutral-600">{t(`booking:invite.${reason}Body`)}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <BookingFlow
        username={data.username}
        slug={data.eventType.slug}
        host={data.host}
        eventType={data.eventType}
        layout="page"
        inviteCode={code}
        prefill={data.prefill}
      />
    </main>
  );
}
